import { useState, useRef, useEffect } from 'react';

function AccordionPanel({ title, headerExtra, defaultOpen = false, children }) {
    const [open, setOpen] = useState(defaultOpen);
    const bodyRef = useRef(null);
    const [height, setHeight] = useState(defaultOpen ? 'auto' : '0px');

    useEffect(() => {
        if (!bodyRef.current) return;
        if (open) {
            const h = bodyRef.current.scrollHeight;
            setHeight(`${h}px`);
            const t = setTimeout(() => setHeight('auto'), 380);
            return () => clearTimeout(t);
        } else {
            const h = bodyRef.current.scrollHeight;
            setHeight(`${h}px`);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setHeight('0px'));
            });
        }
    }, [open]);

    return (
        <div
            className="hj-premium-card"
            style={{
                marginBottom: '1.5rem',
                overflow: 'hidden',
                padding: 0,
                border: '1px solid rgba(0,0,0,0.06)'
            }}
        >
            <div
                style={{
                    padding: '18px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    background: open ? 'var(--co-green-pale)' : 'var(--co-surface)',
                    transition: 'background .3s ease'
                }}
                onClick={() => setOpen(v => !v)}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: 'var(--co-green)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M3 21h18M3 10h18M5 10v11M9 10v11M15 10v11M19 10v11M12 3L2 10h20L12 3z" />
                        </svg>
                    </div>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 800, color: 'var(--co-t1)', margin: 0 }}>{title}</h3>
                        {headerExtra}
                    </div>
                </div>
                <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.04)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform .4s var(--co-ease)',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
                }}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </div>
            </div>

            <div
                ref={bodyRef}
                style={{
                    overflow: 'hidden',
                    height: height,
                    transition: height === 'auto'
                        ? 'none'
                        : 'height .4s var(--co-ease), opacity .3s ease',
                    opacity: open ? 1 : 0,
                    background: 'var(--co-surface)'
                }}
            >
                <div style={{ padding: '0 24px 20px' }}>
                    <div style={{ paddingTop: '16px', borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function BanksPanel({
    banks,
    lang,
    financeSettings,
    config,
    setFinanceSettings,
    setSelectedFinancePeriod,
    reCountFinanceSettings,
    trans,
}) {
    const headerExtra = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {financeSettings?.Image && (
                <div style={{ height: '38px', padding: '4px 12px', background: '#fff', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.08)' }}>
                    <img alt="" style={{ height: '100%', objectFit: 'contain' }} src={config.FilePathURL + financeSettings?.Image} />
                </div>
            )}
            {financeSettings?.DescriptionAr && !financeSettings?.Image && (
                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--co-green)' }}>
                    {lang === 'ar' ? financeSettings.DescriptionAr : financeSettings.DescriptionEn}
                </span>
            )}
        </div>
    );

    return (
        <AccordionPanel
            title={trans('finance:finance')}
            headerExtra={headerExtra}
            defaultOpen={false}
        >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' }}>
                {banks?.map((bank, index) => (
                    <div
                        key={index}
                        className="hj-premium-card"
                        style={{
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '12px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            borderRadius: '16px',
                            border: financeSettings?.DescriptionAr === bank.DescriptionAr ? '2px solid var(--co-green)' : '1px solid rgba(0,0,0,0.06)',
                            background: financeSettings?.DescriptionAr === bank.DescriptionAr ? 'var(--co-green-pale)' : 'var(--co-surface)',
                            boxShadow: financeSettings?.DescriptionAr === bank.DescriptionAr ? '0 8px 24px rgba(24,169,78,.1)' : 'none',
                        }}
                        onClick={() => {
                            const DefaultFinancePeriod = bank.DefaultFinancePeriod ? bank.DefaultFinancePeriod : 60;
                            setFinanceSettings(bank);
                            setSelectedFinancePeriod(DefaultFinancePeriod);
                            reCountFinanceSettings(bank, DefaultFinancePeriod);
                        }}
                    >
                        <div style={{ width: '100%', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {bank?.Image
                                ? <img src={config.FilePathURL + bank.Image} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                : <div style={{ fontSize: '24px' }}>🏦</div>
                            }
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: 800, color: financeSettings?.DescriptionAr === bank.DescriptionAr ? 'var(--co-green)' : 'var(--co-t1)' }}>
                            {lang === 'ar' ? bank.DescriptionAr : bank.DescriptionEn}
                        </span>
                    </div>
                ))}
            </div>
        </AccordionPanel>
    );
}
